
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { CheckCircle, AlertTriangle, AlertCircle, Signal, Activity, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Device } from '@/components/device-health/gis-map/types';

interface SummaryCardsProps {
  latestData: {
    smartMeters: {
      online: number;
      warning: number;
      critical: number;
      offline: number;
      totalReadings: number;
    };
    gateways: {
      online: number;
      warning: number;
      critical: number;
      offline: number;
    };
  };
  selectedSection: string | null;
  onCardClick: (section: string) => void;
  deviceData?: Device | null;
}

const SummaryCards: React.FC<SummaryCardsProps> = ({ 
  latestData, 
  selectedSection, 
  onCardClick,
  deviceData 
}) => {
  // If we have device-specific data, we could customize the display here
  // For now, we'll just continue using the existing implementation
  
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      <Card 
        className={cn(
          "cursor-pointer hover:border-green-500 transition-colors", 
          selectedSection === 'online' && "border-green-500 ring-1 ring-green-500"
        )}
        onClick={() => onCardClick('online')}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Devices Online</p>
              <h3 className="text-2xl font-bold mt-1">
                {latestData.smartMeters.online + latestData.gateways.online}
              </h3>
            </div>
            <div className="rounded-full bg-green-100 p-2 dark:bg-green-900/30">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-end text-xs text-muted-foreground">
            <span>View details</span>
            <ExternalLink className="ml-1 h-3 w-3" />
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className={cn(
          "cursor-pointer hover:border-amber-500 transition-colors",
          selectedSection === 'warning' && "border-amber-500 ring-1 ring-amber-500"
        )}
        onClick={() => onCardClick('warning')}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Devices Warning</p>
              <h3 className="text-2xl font-bold mt-1">
                {latestData.smartMeters.warning + latestData.gateways.warning}
              </h3>
            </div>
            <div className="rounded-full bg-amber-100 p-2 dark:bg-amber-900/30">
              <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-end text-xs text-muted-foreground">
            <span>View details</span>
            <ExternalLink className="ml-1 h-3 w-3" />
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className={cn(
          "cursor-pointer hover:border-red-500 transition-colors",
          selectedSection === 'critical' && "border-red-500 ring-1 ring-red-500"
        )}
        onClick={() => onCardClick('critical')}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Devices Critical</p>
              <h3 className="text-2xl font-bold mt-1">
                {latestData.smartMeters.critical + latestData.gateways.critical}
              </h3>
            </div>
            <div className="rounded-full bg-red-100 p-2 dark:bg-red-900/30">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-end text-xs text-muted-foreground">
            <span>View details</span>
            <ExternalLink className="ml-1 h-3 w-3" />
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className={cn(
          "cursor-pointer hover:border-gray-500 transition-colors",
          selectedSection === 'offline' && "border-gray-500 ring-1 ring-gray-500"
        )}
        onClick={() => onCardClick('offline')}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Devices Offline</p>
              <h3 className="text-2xl font-bold mt-1">
                {latestData.smartMeters.offline + latestData.gateways.offline}
              </h3>
            </div>
            <div className="rounded-full bg-gray-100 p-2 dark:bg-gray-800">
              <Signal className="h-5 w-5 text-gray-600 dark:text-gray-400" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-end text-xs text-muted-foreground">
            <span>View details</span>
            <ExternalLink className="ml-1 h-3 w-3" />
          </div>
        </CardContent>
      </Card>
      
      <Card 
        className={cn(
          "cursor-pointer hover:border-blue-500 transition-colors",
          selectedSection === 'readings' && "border-blue-500 ring-1 ring-blue-500"
        )}
        onClick={() => onCardClick('readings')}
      >
        <CardContent className="p-4">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-sm font-medium">Total Readings</p>
              <h3 className="text-2xl font-bold mt-1">
                {latestData.smartMeters.totalReadings}
              </h3>
            </div>
            <div className="rounded-full bg-blue-100 p-2 dark:bg-blue-900/30">
              <Activity className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-3 flex items-center justify-end text-xs text-muted-foreground">
            <span>View details</span>
            <ExternalLink className="ml-1 h-3 w-3" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SummaryCards;
